import { Stage } from 'konva/lib/Stage';
import SnapManager from '../services/SnapManager';
import SnapLayer from './SnapLayer';
import GuidelineManager from './GuidelineManager';
import GuidelineLayer from './GuidelineLayer';
import LogicLayer from './logic/LogicLayer';
import HassLayer from './hass/HassLayer';

export const DEFAULT_SNAP_SPACING = 20;

export default class ScaleStage extends Stage {
    objectLayer;
    snapLayer;
    guidelineLayer;
    logicLayer;
    hassLayer;
    guidelineManager;
    rulerCanvas = null; // set externally after DOM is ready
    snapManager;
    gridSize = DEFAULT_SNAP_SPACING;
    zoomSensitivity = 1.0;
    minScale = 0.3;
    maxScale = 6;
    lastPinchDist = 0;
    lastPinchCenter = null;
    // container;
    callback;

    constructor(config) {
        super(config);
        this.callback = config.callback;
        if (config.zoom_sensitivity !== undefined) {
            this.zoomSensitivity = config.zoom_sensitivity;
        }

        this.snapLayer = new SnapLayer();
        this.snapManager = new SnapManager();
        this.guidelineManager = new GuidelineManager({ gridSize: this.gridSize });

        // guidelineLayer, objectLayer, snapLayer added via addObjectLayer

//         const origDrawScene = Konva.Layer.prototype.drawScene;

// Konva.Layer.prototype.drawScene = function (...args) {
//     console.trace("Layer.drawScene()");
//     return origDrawScene.apply(this, args);
// };

        this.on('wheel', (e) => {
            this.objectLayer.stageWheelStart();
            e.evt.preventDefault();
            e.evt.stopPropagation();
            this.handleWheel(e);
            this.objectLayer.updateOffPageMask?.();
            this.rulerCanvas?.update();
        });

        this.on('touchmove', (e) => {
            const touch1 = e.evt.touches[0];
            const touch2 = e.evt.touches[1];
            if (!touch1 || !touch2) return;

            e.evt.preventDefault();
            e.evt.stopPropagation();
            this.objectLayer.stageWheelStart();
            if (this.isDragging()) this.stopDrag();

            this.handlePinchZoom(touch1, touch2);
            this.objectLayer.updateOffPageMask?.();
            this.rulerCanvas?.update();
        });

        this.on('touchend', (e) => {
            if (e.evt.touches.length < 2) {
                this.lastPinchDist = 0;
                this.lastPinchCenter = null;
            }
        });

        this.on('dragstart', (e) => {
            e.evt.preventDefault();
            this.objectLayer.stageDragStart();
        });

        this.on('dragmove', (e) => {
            e.evt.preventDefault();
            this.objectLayer.transformer.nodes([]);
            this.objectLayer.updateOffPageMask?.();
            this.rulerCanvas?.update();
        });

        this.on('dragend', (e) => {
            e.evt.preventDefault();
            this.objectLayer.stageDragEnd();
            this.objectLayer.updateOffPageMask?.();
            this.rulerCanvas?.update();
        });
    }

    unScale(val) {
        return (val / this.scaleX());
    }

    handleWheel(e) {
        e.cancelBubble = true;
        const oldScale = this.getStage().scaleX();
        const pointer = this.getStage().getPointerPosition();
        const mousePointTo = {
            x: (pointer.x - this.x()) / oldScale,
            y: (pointer.y - this.y()) / oldScale,
        };

        // when we zoom on trackpad, e.evt.ctrlKey is true; that gesture is
        // already inverted relative to a regular wheel, so flip it back
        const sign = e.evt.ctrlKey ? 1 : -1;

        // Continuous exponential zoom: each pixel of wheel/pinch delta scales
        // the view by a small multiplicative factor instead of jumping
        // between fixed steps, so the zoom feels smooth at any speed.
        const zoomFactor = Math.exp(sign * e.evt.deltaY * this.zoomSensitivity * 0.0015);
        const newScale = Math.min(this.maxScale, Math.max(this.minScale, oldScale * zoomFactor));

        this.scale({ x: newScale, y: newScale });

        const newPos = {
            x: pointer.x - mousePointTo.x * newScale,
            y: pointer.y - mousePointTo.y * newScale,
        };

        this.position(newPos);
        this.callback({ scale: newScale })
    }

    getTouchPoint(touch) {
        const rect = this.container().getBoundingClientRect();
        return { x: touch.clientX - rect.left, y: touch.clientY - rect.top };
    }

    handlePinchZoom(touch1, touch2) {
        const p1 = this.getTouchPoint(touch1);
        const p2 = this.getTouchPoint(touch2);
        const center = { x: (p1.x + p2.x) / 2, y: (p1.y + p2.y) / 2 };
        const dist = Math.hypot(p2.x - p1.x, p2.y - p1.y);

        if (!this.lastPinchCenter) {
            this.lastPinchCenter = center;
            this.lastPinchDist = dist;
            return;
        }

        const oldScale = this.scaleX();
        const pointTo = {
            x: (center.x - this.x()) / oldScale,
            y: (center.y - this.y()) / oldScale,
        };
        const newScale = Math.min(this.maxScale, Math.max(this.minScale, oldScale * (dist / this.lastPinchDist)));

        const dx = center.x - this.lastPinchCenter.x;
        const dy = center.y - this.lastPinchCenter.y;

        this.scale({ x: newScale, y: newScale });
        this.position({
            x: center.x - pointTo.x * newScale + dx,
            y: center.y - pointTo.y * newScale + dy,
        });

        this.lastPinchDist = dist;
        this.lastPinchCenter = center;
        this.callback({ scale: newScale });
    }

    addObjectLayer(objectLayer) {
        this.objectLayer = objectLayer;
        // Layer order: objectLayer → logicLayer (event/action nodes) → hassLayer (Home Assistant
        // flows; one of the two shows, per canvas mode) → guidelineLayer (over objects)
        // → snapLayer (temp snap lines on top)
        this.guidelineLayer = new GuidelineLayer({ guidelineManager: this.guidelineManager, stage: this });
        this.guidelineManager.setOnAttachedObjectsMoved(() => objectLayer.requestRender());
        this.logicLayer?.destroy();
        this.logicLayer = new LogicLayer({ objectLayer });
        this.hassLayer?.destroy();
        this.hassLayer = new HassLayer({ objectLayer });
        this.add(objectLayer);
        this.add(this.logicLayer);
        this.add(this.hassLayer);
        this.add(this.guidelineLayer);
        this.add(this.snapLayer);
        objectLayer.updateOffPageMask?.();
    }

    setZoomSensitivity(sensitivity) {
        this.zoomSensitivity = sensitivity;
    }

    getAbsolutePos(node) {
        let pos = node.getAbsolutePosition();
        pos.x = (pos.x - this.x()) / this.scale().x;
        pos.y = (pos.y - this.y()) / this.scale().y;
        return pos;
    }

}
